import React from 'react';

// a widget's settings, keyed by SettingControl.key (toggles are booleans, everything else strings)
export type SettingValues = Record<string, string | boolean>;

// one row of the Settings card; WidgetDetail renders them in order
export type SettingControl =
    | { kind: 'heading'; label: string }
    | { kind: 'toggle'; key: string; label: string; hint?: string; default: boolean }
    // stacked = full-width segmented under an optional label; inline = label on the left, options on the right
    | { kind: 'segmented'; key: string; label?: string; options: string[]; default: string; inline?: boolean; disabled?: (s: SettingValues) => boolean }
    | { kind: 'text'; key: string; label: string; default: string; multiline?: boolean }
    | { kind: 'select'; key: string; label: string; options: string[]; default: string }
    | { kind: 'color'; key: string; label?: string; hint?: string; default: string }
    | { kind: 'note'; text: string }
    // anything the kinds above can't express (a group of colour pickers, the welcome-email card)
    | { kind: 'custom'; render: React.FC<{ settings: SettingValues; set: (key: string, value: string | boolean) => void }> };

// a block inside "Add It to a Page"
export type AddBlock =
    | { title: string; code: string; dark?: boolean; after?: string }
    // a stack of small dark codes, each with a label (e.g. "Or one group at a time")
    | { title: string; codes: { label: string; code: string }[]; after?: string }
    | { title: string; text: string }
    // links to the full-width sections under the preview (capital structure)
    | { title: string; options: { title: string; hint: string }[]; after?: string };

export interface WidgetDef {
    slug: string;
    // the list row: name + one-line description, and its icon tile
    name: string;
    description: string;
    icon: React.ReactNode;
    // detail page title, e.g. "Share Price Widget"
    title: string;
    installed: boolean;
    // where Investi found it (null = not on the website yet)
    page: string | null;
    // list row when not installed: the page it should go on ("Install Now"), or nothing ("Not found on the website")
    expectedPage?: string;
    // extra line on the list row's "where" cell when there's no page (e.g. "Site header · every page")
    where?: string;
    // not installed + must be added: the list row shows a primary Install Now instead of Get Code
    mustInstall?: boolean;
    views: number | null;
    // the header line after the status pill when installed (defaults to "On {page} · N views in 30 days")
    summary?: string;
    // the meta bar's status pill (defaults to "Investi can see it on the page")
    metaStatus?: string;
    // the meta bar's first label (default "Page")
    metaLabel?: string;
    // header shows "Get the Code" beside "View on the Website"
    getCodeButton?: boolean;
    savedCodes?: { path: string; note: string }[];
    // which saved code is highlighted (index); default the last
    savedCodeSelected?: number;
    // the page shown in the demo's browser bar (path only; the client's host is added)
    demoPath: string;
    // the green "how this code works" note at the top of Add It to a Page
    addNote?: string;
    // default: shortcode + plain HTML
    addBlocks: AddBlock[];
    // the green footer note (default: stylesheet and script are already on the website)
    addFooter?: string;
    settings?: SettingControl[];
    // the line under Generate the Code
    generateNote?: string;
    // replaces the Settings card (company details "One Code per Detail")
    Side?: React.FC;
    // full-width sections under the preview row (capital structure)
    After?: React.FC;
    Demo: React.FC<{ settings: SettingValues }>;
}

export const settingDefaults = (controls: SettingControl[] = []): SettingValues => {
    const values: SettingValues = {};
    controls.forEach(c => {
        if ('key' in c && 'default' in c) values[c.key] = c.default;
    });
    return values;
};

// the default footer under "Add It to a Page"
export const ALREADY_LOADED = 'The Investi stylesheet and script are already on your website — added once in step 1, Connect.';
// the default line under Generate the Code
export const GENERATE_NOTE = 'Change the settings and press Generate again to make a second code for another page. Each saved code above keeps its own style — pick one to load its settings.';
export const GENERATE_NOTE_SINGLE = 'Builds the code for these settings. This widget uses one style everywhere it appears.';
